/**
 * Inline accept/decline dialog for a loan proposal.
 * Opened from NotificationCenter when a loan_proposal_received notification is clicked.
 */
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { T, RADIUS, SHADOW } from '@/lib/tokens';
import { useAcceptLoanProposal, useCancelLoanProposal } from '@/hooks/useLoans';
import { useNetworkStatusContext } from '@/contexts/NetworkStatusContext';
import { toast } from 'sonner';

interface AcceptLoanProposalDialogProps {
  loanId: string | null;
  itemName?: string;
  lenderName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 *
 * @param root0
 * @param root0.loanId
 * @param root0.itemName
 * @param root0.lenderName
 * @param root0.open
 * @param root0.onOpenChange
 */
export function AcceptLoanProposalDialog({
  loanId,
  itemName,
  lenderName,
  open,
  onOpenChange,
}: AcceptLoanProposalDialogProps) {
  const acceptProposal = useAcceptLoanProposal();
  const cancelProposal = useCancelLoanProposal();
  const { isOnline } = useNetworkStatusContext();

  const isPending = acceptProposal.isPending || cancelProposal.isPending;

  const handleAccept = async () => {
    if (!loanId) return;
    try {
      await acceptProposal.mutateAsync({ loanId });
      toast.success('Loan accepted');
      onOpenChange(false);
    } catch (err) {
      const msg = (err as Error)?.message ?? '';
      if (msg.includes('not found') || msg.includes('not awaiting')) {
        toast.error('This loan offer is no longer available.');
      }
      onOpenChange(false);
    }
  };

  const handleDecline = async () => {
    if (!loanId) return;
    try {
      await cancelProposal.mutateAsync({ loanId });
      toast.success('Loan declined');
      onOpenChange(false);
    } catch (err) {
      const msg = (err as Error)?.message ?? '';
      if (msg.includes('not found') || msg.includes('not awaiting')) {
        toast.error('This loan offer is no longer available.');
      }
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Loan Offer</DialogTitle>
        </DialogHeader>
        <p style={{ fontSize: 14, color: T.text, padding: '4px 0' }}>
          {lenderName ?? 'Someone'} wants to lend you <strong>{itemName ?? 'an item'}</strong>.
        </p>
        <p style={{ fontSize: 13, color: T.textMuted, margin: 0 }}>
          Accept to start the loan, or decline to pass.
        </p>
        <DialogFooter className="gap-2">
          <button
            onClick={handleDecline}
            disabled={isPending || !isOnline}
            title={!isOnline ? "You're offline" : undefined}
            style={{
              padding: '10px 18px',
              borderRadius: RADIUS.md,
              background: T.surfaceSubtle,
              color: T.textSec,
              border: `1px solid ${T.border}`,
              fontSize: 14,
              fontWeight: 600,
              cursor: isPending || !isOnline ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              opacity: isPending || !isOnline ? 0.6 : 1,
            }}
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            disabled={isPending || !isOnline}
            title={!isOnline ? "You're offline" : undefined}
            style={{
              padding: '10px 18px',
              borderRadius: RADIUS.md,
              background: isPending || !isOnline ? T.surfaceSubtle : T.accent,
              color: isPending || !isOnline ? T.textMuted : T.white,
              border: 'none',
              fontSize: 14,
              fontWeight: 600,
              cursor: isPending || !isOnline ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              boxShadow: isPending || !isOnline ? 'none' : SHADOW.btn.accent,
            }}
          >
            Accept
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
